/**
 * The track-list star: one cell, one click, and no idea what a track list is.
 *
 * This renders a glyph and reports a click. It does not know which lists exist,
 * cannot add or remove a symbol, and never opens the menu itself — the host does
 * all three. That is not fastidiousness: this package is hosted inside a WebView
 * on three platforms and **may not import from the web app**, where `Menu` and
 * `ContextMenu` live. So the seam is the same one `resolveToken` and
 * `compileExpr` already use — the island renders, the host decides.
 *
 * ## What the marks mean
 *
 * `marks` is what the host says this symbol's membership looks like: nothing, or
 * one colour per list it belongs to. The cell draws a filled star plus a pip per
 * *further* list, capped, because a symbol in twelve lists is not twelve
 * readable pips — it is a smear. The menu is the readout; this is the glance.
 *
 * ## The click has to be swallowed
 *
 * A row click in this grid selects the row, and selecting is what charts the
 * symbol. A click inside a cell reaches the row handler too, so without
 * `stopPropagation` opening the menu would also re-chart — which reads as a bug
 * because it is one. The removed row-actions column had exactly this line in it,
 * and it was the load-bearing part.
 *
 * `onMouseDown` is stopped as well as `onClick`. AG Grid begins its selection on
 * mousedown, so stopping only the click leaves the row selected before the menu
 * is even open — the visible symptom being that clicking a star inside a
 * multi-row selection silently collapses it to one row, which is precisely the
 * gesture the bulk path depends on.
 */

import type { CSSProperties } from "react";

/** One list this symbol is in, as far as the cell needs to know. */
export interface TrackMark {
  /** Stable id, for React keys only — the cell never resolves it to anything. */
  id: string;
  /** A resolved CSS colour. The host owns the theme; this just paints it. */
  colour: string;
}

/** Pips drawn beside the star before the rest become a `+n`. */
const MAX_PIPS = 2;

export interface TrackListCellProps {
  /** The row, as AG Grid hands it over. */
  data?: Record<string, unknown>;
  /** Which field on the row is the symbol. */
  idField?: string;
  /**
   * This symbol's membership. Empty means "in no list".
   *
   * A **function**, not a value, and that is what keeps the column cheap: AG
   * Grid holds `cellRendererParams` by identity, so a params object carrying
   * the membership itself would have to be rebuilt on every add — and rebuilding
   * it rebuilds the column definitions, which rebuilds the grid. A stable getter
   * reading the host's current state is called at render time instead, and a
   * repaint is `refreshCells` on one column.
   */
  getMarks?: (symbol: string) => readonly TrackMark[];
  /**
   * The star was clicked.
   *
   * `alt` travels because the host binds the two gestures differently — a plain
   * click opens the menu, alt-click drops the row straight into the default list
   * — and the modifier is only knowable at the event.
   *
   * The anchor rectangle goes with it because the host positions a fixed-position
   * popover and has no other way to find this cell.
   */
  onOpen?: (symbol: string, anchor: DOMRect, alt: boolean) => void;
}

const WRAP: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  width: "100%",
  height: "100%",
  cursor: "pointer",
  // The hollow star only on hover, so an empty column costs no ink. A CSS
  // variable rather than a hover state in React: re-rendering a cell on
  // pointerenter is a render per row per mouse move across the grid.
  lineHeight: 1,
};

const STAR: CSSProperties = { fontSize: 13, lineHeight: 1 };

const PIP: CSSProperties = {
  width: 5,
  height: 5,
  borderRadius: 1,
  display: "inline-block",
};

const MORE: CSSProperties = {
  fontSize: 8,
  opacity: 0.75,
  fontVariantNumeric: "tabular-nums",
};

export function TrackListCell({ data, idField, getMarks, onOpen }: TrackListCellProps) {
  const raw = idField ? data?.[idField] : undefined;
  const symbol = typeof raw === "string" ? raw : "";
  // A rollup row's key is a sector, not a symbol, and putting a sector on a
  // watchlist is not a thing. `ScreenerPanel` already withholds the column
  // there; this is the second guard, for the frame that has no id field at all.
  if (!symbol) return null;

  const list = getMarks?.(symbol) ?? [];
  const pips = list.slice(1, 1 + MAX_PIPS);
  const rest = Math.max(0, list.length - 1 - pips.length);

  return (
    <div
      style={WRAP}
      className="qf-track-cell"
      title={list.length ? undefined : "Add to a list"}
      onMouseDown={(event) => {
        // Before AG Grid starts a selection — see the header.
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        onOpen?.(
          symbol,
          (event.currentTarget as HTMLElement).getBoundingClientRect(),
          event.altKey,
        );
      }}
    >
      {list.length > 0 ? (
        <span style={{ ...STAR, color: list[0]!.colour }}>&#9733;</span>
      ) : (
        // Hollow, and faded until the row is hovered — the rule lives in the
        // island's stylesheet so hovering a row is not a React render.
        <span style={STAR} className="qf-track-empty">
          &#9734;
        </span>
      )}
      {pips.map((mark) => (
        <i key={mark.id} style={{ ...PIP, background: mark.colour }} />
      ))}
      {rest > 0 ? <span style={MORE}>+{rest}</span> : null}
    </div>
  );
}
